import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import SavedFilterVisibility from "@mapmycustomers/shared/enum/SavedFilterVisibility";
import { EntityType, EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Team from "@mapmycustomers/shared/types/Team";

import useMapView from "@app/component/view/utils/useMapView";
import { RootState } from "@app/store/rootReducer";
import { getFavoriteSavedFilters, isSavedFilterCreateLoading } from "@app/store/savedFilters";
import { createSavedFilter } from "@app/store/savedFilters/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import { FILTER_ENTITY_TYPES } from "../../utils/consts";

import AddEditFilterModal from "./components/AddEditFilterModal";
import { MAX_FAVORITE_FILTERS } from "./util/consts";

export interface Props {
  createSavedFilter: typeof createSavedFilter.request;
  entityType: EntityType.PIN;
  implicitFilterFields?: IField[];
  loading: boolean;
  onHide: () => void;
  reachedFavoriteFiltersLimit: boolean;
  visible: boolean;
}

const CreateFilterModal: React.FC<Props> = ({
  createSavedFilter,
  entityType,
  implicitFilterFields,
  loading,
  onHide,
  reachedFavoriteFiltersLimit,
  visible,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Create Saved Filter", useAnalytics());
  const { viewState } = useMapView();

  const { columns, filter, viewAs } = viewState;

  const handleHide = useCallback(() => {
    onHide();
    analytics.cancelled(["Cancel"]);
  }, [analytics, onHide]);

  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const handleSave = useCallback(
    (name: string, visibility: SavedFilterVisibility, favorite: boolean, teamId: Team["id"][]) => {
      const filtersToSave = { ...filter };
      // remove all implicit fields' filters
      FILTER_ENTITY_TYPES.forEach((entityType) => {
        if (filtersToSave[entityType]) {
          (implicitFilterFields ?? []).forEach(({ filterName }) => {
            delete filtersToSave[entityType]![filterName];
          });
        }
      });

      createSavedFilter({
        callback: onHide,
        columns,
        favorite,
        filter: { filters: filtersToSave, name, teamId, type: entityType, viewAs, visibility },
      });
      analytics.completed(["Save Filter"], { filter });
    },
    [
      analytics,
      createSavedFilter,
      columns,
      filter,
      implicitFilterFields,
      onHide,
      entityType,
      viewAs,
    ]
  );

  return visible ? (
    <AddEditFilterModal
      loading={loading}
      okButtonText={intl.formatMessage({
        id: "savedFilters.modal.create.button",
        defaultMessage: "Create Saved Filter",
        description: "Saved Filter create modal create button text",
      })}
      onHide={handleHide}
      onSave={handleSave}
      reachedFavoriteFiltersLimit={reachedFavoriteFiltersLimit}
      title={intl.formatMessage({
        id: "savedFilters.modal.create.title",
        defaultMessage: "Create Saved Filter",
        description: "Saved Filter create modal create text",
      })}
    />
  ) : null;
};

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: EntityTypesSupportingSavedFilters }
) => ({
  loading: isSavedFilterCreateLoading(state),
  reachedFavoriteFiltersLimit:
    getFavoriteSavedFilters(state)(ownProps.entityType).length >= MAX_FAVORITE_FILTERS,
});

const mapDispatchToProps = {
  createSavedFilter: createSavedFilter.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateFilterModal);
